import {
  CutlistMaterialGroup,
  CutlistOrder,
  CutlistPart,
  MaterialGroup,
  PartItem,
  SheetSizeSelectionOption,
} from '@cutr/constants/cutlist';
import type { Ticker } from '@cutr/constants/cutlist-theme.ts';
import { SMALL_PARTS_EDGEBANDING_FLAG } from '@cutr/constants/small-part-edgebanding';
import type { NestingOutput } from '@cutr/nesting';
import { useFlag } from '@unleash/proxy-client-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useLeadDetails } from './api/account';
import { useDeliveryAddress } from './api/address';
import { api } from './api/backend';
import { resetErrors, usePartsHaveErrors } from './api/errors';
import { AuthState, useAuthStore, useIsLoggedIn } from './api/login';
import {
  cacheMaterials,
  getMaterial,
  getMaterialById,
  materialsCached,
} from './api/materials';
import {
  articleCodeToMaterialGroupMapper,
  findBeamMaterial,
  hashGroup,
  useGroupBy,
  useMaterialGroupState,
} from './api/materialsGroup';
import {
  computeCutlistNestingHash,
  isCutlistFullyNested,
  persistedNestingFailures,
  useMixedNestingStore,
} from './api/mixedNesting';
import { useNestingStatusStore, useNestingStore } from './api/nesting';
import {
  conditionallyTogglePricing,
  usePriceExVat,
  usePricingStore,
} from './api/pricing';
import {
  isValidBeamPart,
  partsSelector,
  useCutlistParts,
  useCutlistState,
  useSetTitle,
  useTitle,
} from './api/store';
import { useCreateCutlist } from './queries/crud';
import {
  getCurrentFeatures,
  getSourceConfig,
  isStarterTier,
  useCurrentFeatures,
  useTicker,
} from './theme';
import { BeamSize, getBeamSizes } from './utils/beamSizes';
import {
  isInIframe,
  resolveTokenLoginTarget,
  urlParamToBoolean,
} from './utils/misc';
import { getManualEdgebandingSummary } from './utils/smallPartEdgebanding';
import { SessionStorage } from './utils/storage';

export const selectOnFocus = (input: HTMLInputElement) => {
  if (window.matchMedia('(pointer: coarse)').matches) return;
  input.select?.();
};

export const onFocusSelect = (e: React.FocusEvent<HTMLInputElement>) => {
  const i = e.currentTarget;
  setTimeout(() => selectOnFocus(i), 0);
};

// restore focus to the last element when the modal closes
export function useFocusRestore(restoreFocus: boolean) {
  const lastFocussedElement = React.useRef<HTMLElement | null>(null);
  React.useLayoutEffect(() => {
    if (!restoreFocus) {
      lastFocussedElement.current = document.activeElement as HTMLElement;
      return;
    }
    lastFocussedElement.current?.focus();
    lastFocussedElement.current = null;
  }, [restoreFocus]);
}

const CUTLIST_TITLE_MAP: { [key: string]: string } = {
  nl: 'Zaaglijst',
  en: 'Cutlist',
  de: 'Zuschnittsliste',
};
export function useInitCutlist() {
  const { i18n } = useTranslation();
  const isDirty = React.useRef(false);
  const title = useTitle();
  const setTitle = useSetTitle();

  useGroupByMaterial();

  React.useEffect(() => {
    // wait for language
    if (!i18n.resolvedLanguage) return;

    setTimeout(() => (isDirty.current = true), 0);
    if (title || isDirty.current) return;

    setTitle(CUTLIST_TITLE_MAP[i18n.resolvedLanguage]);
  }, [i18n.resolvedLanguage, title, setTitle]);
}

export function useGroupByMaterial() {
  const groupBy = useGroupBy();

  React.useEffect(() => {
    groupBy();
  }, [groupBy]);
}

export function useApplyTheme(themeOverride?: Ticker) {
  const ticker = useTicker();
  const config = getSourceConfig();

  const themeToUse = themeOverride || ticker;
  const namespace = config.i18nNamespace || themeToUse.toLowerCase();
  const { i18n } = useTranslation();
  if (!i18n.hasLoadedNamespace(namespace)) {
    i18n.loadNamespaces(namespace);
  }
  i18n.setDefaultNamespace(namespace);

  if (isInIframe()) {
    document.body.setAttribute('data-embedded', '');
  }

  React.useEffect(() => {
    SessionStorage.set('ticker', themeToUse);
    document.body.setAttribute('data-theme', themeToUse);
    document
      .querySelector('link[rel=icon]')
      ?.setAttribute('href', config.favicon);
  }, [themeToUse, config.favicon]);
}

export function useFetchMaterials() {
  const isLoggedIn = useIsLoggedIn();
  const setHasMaterials = useCutlistState((state) => state.setHasMaterials);
  React.useEffect(() => {
    if (materialsCached()) return;

    api
      .materials()
      .then(cacheMaterials)
      .then(() => setHasMaterials(true));
  }, [isLoggedIn, setHasMaterials]);
}

export function useLoginByTokenParam() {
  const [searchParams] = useSearchParams();
  const { login } = useAuthStore();
  const inputToken = searchParams.get('token') || '';
  const cutlistId = searchParams.get('cutlistId') || '';
  const redirect = searchParams.get('redirect') || '';
  const isLoggedIn = useIsLoggedIn();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!inputToken) return;

    const handleLoginByToken = async () => {
      if (!isLoggedIn) {
        const data = await api.loginByToken(inputToken);
        const { email, clientNumber, token, userLeadId } = data;
        login({ email, clientNumber, token, userLeadId });
      }

      navigate(resolveTokenLoginTarget(redirect, cutlistId));
      window.analytics.track('Navigated to Cutlist View-Only', {
        cutlistId,
        alreadyLoggedIn: false,
      });
    };

    handleLoginByToken();
  }, [inputToken, isLoggedIn, login, cutlistId, redirect, navigate]);
}

export function useLoginByKeyParam() {
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key');
  const { mutateAsync: createCutlist } = useCreateCutlist();

  const { login } = useAuthStore();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!key) return;

    const handleCall = async () => {
      const data = await api.loginByKey(key);
      const { email, clientNumber, token, userLeadId } = data as AuthState;
      login({ email, clientNumber, token, userLeadId });
      searchParams.delete('key');

      const cutlist = await createCutlist();

      navigate(`/cutlist/${cutlist.id}/parts?${searchParams.toString()}`);
    };

    handleCall();
  }, [key, createCutlist, login, searchParams, navigate]);
}

export function useThirdPartyLogin() {
  const [searchParams] = useSearchParams();
  const { login } = useAuthStore();
  const navigate = useNavigate();
  const [error, setError] = React.useState<unknown>();
  React.useEffect(() => {
    const handleCall = async () => {
      try {
        const tokenParam = searchParams.get('token');

        if (!tokenParam) {
          setError('Token is missing');
          return;
        }

        const data = await api.thirdPartyLogin(tokenParam);
        const {
          email,
          clientNumber,
          token,
          userLeadId,
          cutlistId,
          missingArticleCodes,
        } = data;
        login({ email, clientNumber, token, userLeadId });

        searchParams.delete('token');

        navigate(`/cutlist/${cutlistId}/parts?${searchParams.toString()}`, {
          state: { missingArticleCodes },
          replace: true,
        });
      } catch (err) {
        setError(err);
      }
    };

    handleCall();
  }, [searchParams, login, navigate, setError]);

  return { error };
}

export function usePageViews() {
  const location = useLocation();
  const ticker = useTicker();

  React.useEffect(() => {
    window.analytics.page(undefined, undefined, { ticker });
  }, [location, ticker]);
}

export function useTogglePricing() {
  const { showPricing } = useCurrentFeatures();
  const urlParams = new URLSearchParams(window.location.search);
  const pricingParam = urlParamToBoolean(urlParams.get('p'));
  const noOption = pricingParam == null;

  if (noOption) return conditionallyTogglePricing(showPricing);

  conditionallyTogglePricing(pricingParam);
}

export const useBeamSizes = (enabled: boolean) => {
  const [beamSizes, setBeamSizes] = React.useState<BeamSize[]>([]);

  React.useEffect(() => {
    if (!enabled) return;
    api.beamMaterials({}).then((materials) => {
      setBeamSizes(getBeamSizes(materials || []));
    });
  }, [enabled]);

  return beamSizes;
};

export const useIsAutoNestingRunning = () => {
  const nestingStatus = useNestingStatusStore((state) => state.status);
  const materialGroupState = useMaterialGroupState();

  return materialGroupState.groups.some(
    (group) =>
      group.sheetSizeSelection === 'automatic' &&
      nestingStatus[group.id] === 'running'
  );
};

export const useManualEdgebanding = () => {
  const isEnabled = useFlag(SMALL_PARTS_EDGEBANDING_FLAG);
  const { manualEdgebandingRule } = useCurrentFeatures();
  const parts = useCutlistParts();

  return getManualEdgebandingSummary(
    isEnabled ? parts : [],
    manualEdgebandingRule
  );
};

export const useIsNextDisabled = () => {
  const nestingSheetGroups = useNestingStore((state) => state.sheetGroups);
  const materialGroupState = useMaterialGroupState();
  const hasSheetMaterialGroups = materialGroupState.groups.some(
    (group) => group.type !== 'beams'
  );
  const parts = useCutlistParts();
  const partsHaveErrors = usePartsHaveErrors();
  const totalAmountExclVAT = usePriceExVat();
  const { showPricing } = useCurrentFeatures();
  const isLoggedIn = useIsLoggedIn();
  const isAutoNestingRunning = useIsAutoNestingRunning();
  const { isBlocked: isManualEdgebandingBlocked } = useManualEdgebanding();

  const sheetsAwaitingNesting =
    hasSheetMaterialGroups && nestingSheetGroups.length === 0;

  // We can't price beams without a material assigned but users can still request a quote for them.
  const hasOnlyBeamPartsWithoutMaterial =
    parts.length > 0 &&
    parts.every(
      (part) =>
        part.partType === 'beam' &&
        part.beamMaterialId === null &&
        isValidBeamPart(part)
    );

  const isPriceUnavailable =
    isLoggedIn &&
    showPricing &&
    !totalAmountExclVAT &&
    !hasOnlyBeamPartsWithoutMaterial;

  const isNextDisabled =
    isPriceUnavailable ||
    partsHaveErrors ||
    isManualEdgebandingBlocked ||
    sheetsAwaitingNesting ||
    isAutoNestingRunning;

  return isNextDisabled;
};

export const resetStoreFn = () => {
  const priceStore = usePricingStore.getState();
  const cutlistStore = useCutlistState.getState();
  const userLeadStore = useLeadDetails.getState();
  const groupStore = useMaterialGroupState.getState();
  const deliveryAddressStore = useDeliveryAddress.getState();
  const nestingStore = useNestingStore.getState();
  const nestingStatusStore = useNestingStatusStore.getState();
  const mixedNestingStore = useMixedNestingStore.getState();

  return () => {
    groupStore.clear();
    cutlistStore.reset();
    userLeadStore.reset();
    deliveryAddressStore.reset();
    priceStore.reset();
    nestingStore.setNesting([]);
    nestingStore.setInitialNestingOutputs({});
    nestingStatusStore.resetProgress();
    mixedNestingStore.reset();
    resetErrors();
  };
};

type HydrateStoreOptions = {
  activeGroupArticleCode?: string; // pass an article code to activate the first group that contains it
  activeGroupId?: string; // pass a group id to activate it
  missingMaterials?: string[];
};

export const hydrateStoreFn = () => {
  const cutlistStore = useCutlistState.getState();
  const groupStore = useMaterialGroupState.getState();
  const userLeadStore = useLeadDetails.getState();
  const deliveryAddressStore = useDeliveryAddress.getState();
  const nestingStore = useNestingStore.getState();
  const priceStore = usePricingStore.getState();
  const resetStore = resetStoreFn();
  const currentFeatures = getCurrentFeatures();

  const hydrateStore = async (
    cutlist: CutlistOrder,
    options: HydrateStoreOptions = {}
  ) => {
    resetStore();

    priceStore.setAmountVat(cutlist.totalAmountInclVAT);
    priceStore.setAmountNoVat(cutlist.totalAmountExclVAT);
    priceStore.setPricing(cutlist.pricingLineItems);
    priceStore.toggleManualQuote(cutlist.manualQuote);

    const parts = cutlist.materialGroups
      .flatMap((g) => g.parts)
      .sort((partA, partB) => partA.index - partB.index)
      .map(partMapper);

    cutlistStore.init({
      orderId: cutlist.id,
      shortId: cutlist.shortId,
      vatRate: cutlist.vatRate,
      currency: cutlist.currency,
      sourceCurrency: cutlist.sourceCurrency,
      exchangeRate: cutlist.exchangeRate,
      title: cutlist.title || '',
      customerReference: cutlist.customerReference || '',
      internalPointOfContact: cutlist.internalPointOfContact || '',
      deliverLeftoverMaterials: cutlist.deliverLeftoverMaterials,
      notes: cutlist.notes || '',
      status: cutlist.status,
      requestedDeliveryDate: cutlist.requestedDeliveryDate?.split('T')[0] || '', // drop time and keep date
      discountAmount: cutlist.discountAmount || 0,
      discountPercentage: cutlist.discountPercentage || 0,
      markupAmount: cutlist.markupAmount || 0,
      markupPercentage: cutlist.markupPercentage || 0,
      authorAgent: cutlist.authorAgent || undefined,
      parts,
      thirdPartyId: cutlist.thirdPartyId || '',
    });

    if (cutlist.materialGroups.length) {
      const missingGroups = await Promise.all(
        (options.missingMaterials || []).map((articleCode) =>
          articleCodeToMaterialGroupMapper(articleCode, currentFeatures)
        )
      );

      const groups = cutlist.materialGroups
        .map((g) => materialGroupMapper(g))
        .concat(missingGroups);

      const activeGroupIdFromArticleCode =
        options.activeGroupArticleCode &&
        groups.find((g) => g.core1 === options.activeGroupArticleCode)?.id;

      const activeGroupId = options.activeGroupId
        ? cutlist.materialGroups.find(({ id }) => id === options.activeGroupId)
            ?.groupId
        : undefined;

      const activeGroup =
        activeGroupId ||
        activeGroupIdFromArticleCode ||
        cutlist.materialGroups[0]?.groupId;

      groupStore.init({ activeGroup, groups });

      const initialNestingOutputs = Object.fromEntries(
        cutlist.materialGroups
          .filter((g) => g.nestingOutput && 'sheets' in g.nestingOutput)
          .map((g) => [g.groupId, g.nestingOutput as NestingOutput])
      );
      nestingStore.setInitialNestingOutputs(initialNestingOutputs);
    } else if (options.missingMaterials?.length) {
      const missingBeamMaterials = options.missingMaterials.filter(
        (material) => !getMaterial(material)
      );
      const missingSheetMaterials = options.missingMaterials.filter(
        (material) => getMaterial(material)
      );

      for (const material of missingBeamMaterials) {
        const beamMaterial = await findBeamMaterial(material);
        if (beamMaterial) {
          groupStore.addBeamGroupByArticleCode(beamMaterial);
        }
      }

      groupStore.addGroupsByArticleCodes(
        missingSheetMaterials,
        currentFeatures,
        options.activeGroupArticleCode
      );
    } else {
      groupStore.addGroup();
    }

    if (isStarterTier()) {
      const mixedNestingStore = useMixedNestingStore.getState();
      mixedNestingStore.setResults(cutlist);

      const failedGroups = persistedNestingFailures(cutlist);
      const hasError = failedGroups.length > 0;
      mixedNestingStore.setFailedGroups(hasError ? failedGroups : null);
      if (hasError || isCutlistFullyNested(cutlist)) {
        mixedNestingStore.setSnapshotHash(
          computeCutlistNestingHash(
            useMaterialGroupState.getState().groups,
            partsSelector(useCutlistState.getState())
          )
        );
        mixedNestingStore.setNestingState(hasError ? 'failed' : 'done');
      }
    }

    const { userLeadDetail, userLead } = cutlist;

    const deliveryAddress = cutlist.addresses[0];
    if (deliveryAddress) {
      deliveryAddressStore.init({
        ...deliveryAddress,
        contactName: deliveryAddress.name,
      });
    }

    userLeadStore.init({
      ...userLeadDetail,
      ...userLead,
      name: userLead?.name || userLeadDetail?.name,
      // @ts-ignore
      clientNumber: userLead?.clientNumber || userLeadDetail?.clientNumber,
      pricingCategory: userLead?.pricingCategory || undefined,
      customerType: userLead?.customerType || undefined,
      companyName: userLead?.companyName || userLeadDetail?.companyName,
      notClient: false,
    });
  };

  return hydrateStore;
};

export function materialGroupMapper(g: CutlistMaterialGroup): MaterialGroup {
  const type = g.parts.every((part) => part.partType === 'beam')
    ? ('beams' as const)
    : g.parts.every((part) => part.partType === 'sheet')
      ? ('sheets-only' as const)
      : ('panels-and-strips' as const);

  const sheetEdgeTrimConfig = {
    sheetEdgeTrimType: g.sheetEdgeTrimType,
    trimThickness: {
      length1TrimThicknessMM: g.length1TrimThicknessMM || 0,
      length2TrimThicknessMM: g.length2TrimThicknessMM || 0,
      width1TrimThicknessMM: g.width1TrimThicknessMM || 0,
      width2TrimThicknessMM: g.width2TrimThicknessMM || 0,
    },
  };

  const newGroup = {
    ulid: g.id,
    id: g.groupId,
    index: g.index,
    name: g.name,
    edgeProfile: g.edgeProfileType,
    continuousGrain: g.continuousGrain || '',
    additionalProcessing: g.additionalProcessing || '',
    core1: g.core1Material?.articleCode || null,
    core2: g.core2Material?.articleCode || null,
    topHpl: g.topHpl?.articleCode || null,
    bottomHpl: g.bottomHpl?.articleCode || null,
    edgeband: g.edgeband?.articleCode || null,
    materialSandwichType: g.materialSandwichType,
    sheetEdgeTrimConfig,
    sheetSizeSelection: (g.sheetSizeSelection ??
      'manual') as SheetSizeSelectionOption,
    automaticSheetSizeMaterials: (g.automaticSheetSizeMaterialIds ?? [])
      .map((id) => getMaterialById(id)?.articleCode)
      .filter((code): code is string => code !== undefined),
    paintColor: g.paintColor,
    sheetStackingType: g.sheetStackingType,
    createLabels: g.createLabels,
    paintThicknessUM: g.paintThicknessUM,
    aiSuggestions: g.aiSuggestions,
    beamMaterialId: g.beamMaterialId,
    type,
  };

  const groupParts = g.parts.map(partMapper);

  const hash = hashGroup(newGroup, groupParts);

  return { ...newGroup, hash };
}

export const partMapper = (part: CutlistPart): PartItem => {
  return {
    id: part.id,
    index: part.index,
    groupId: part.groupId,
    shortId: part.shortId,
    label: part.label,
    quantity: part.quantity,
    grooves: part.grooves,
    edgebanding: {
      length1: part.length1Edgeband?.articleCode || null,
      length2: part.length2Edgeband?.articleCode || null,
      width1: part.width1Edgeband?.articleCode || null,
      width2: part.width2Edgeband?.articleCode || null,
    },
    roundedEdgeband: {
      length1: part.length1RoundedEdgeband,
      length2: part.length2RoundedEdgeband,
      width1: part.width1RoundedEdgeband,
      width2: part.width2RoundedEdgeband,
    },
    edgeProfile: {
      length1: part.length1EdgeProfile || 'none',
      length2: part.length2EdgeProfile || 'none',
      width1: part.width1EdgeProfile || 'none',
      width2: part.width2EdgeProfile || 'none',
    },
    grainDirection: part.grainDirection,
    createLabel: part.createLabel,
    widthMM: part.widthMM,
    lengthMM: part.lengthMM,
    thickness: part.thicknessUM,
    cncSeconds: part.cncSeconds,
    core1: part.core1Material?.articleCode || null,
    core2: part.core2Material?.articleCode || null,
    topHpl: part.topHpl?.articleCode || null,
    bottomHpl: part.bottomHpl?.articleCode || null,
    partType: part.partType,
    aiSuggestions: part.aiSuggestions || {},
    aiMeta: part.aiMeta || {},
    beamMaterialId: part.beamMaterialId || null,
    beamRounded: part.beamRounded,
  };
};
